import {
  Form,
  Formik,
  type FormikErrors,
  type FormikProps,
  type FormikTouched,
} from "formik"
import { Button } from "primereact/button"
import { classNames } from "primereact/utils"
import { type FC, useEffect, useRef, useState } from "react"

type StepAdvanceContext = {
  rollback: () => void
}

type StepComponentProps<T extends Record<string, unknown>> = FormikProps<T> & {
  onNext: () => void
  onBack: () => void
  isFirstStep: boolean
  isLastStep: boolean
  currentStepIndex: number
  totalSteps: number
}

type FooterActionContext<T extends Record<string, unknown>> = Pick<
  StepComponentProps<T>,
  "onNext" | "onBack" | "isSubmitting"
> & {
  requestSubmit: () => void
}

type FooterAction<T extends Record<string, unknown>> = {
  label: string
  type?: "button" | "submit"
  className?: string
  loading?: boolean | ((context: FooterActionContext<T>) => boolean)
  disabled?: boolean | ((context: FooterActionContext<T>) => boolean)
  hidden?: boolean | ((context: FooterActionContext<T>) => boolean)
  onClick?: (context: FooterActionContext<T>) => void
}

type Step<T extends Record<string, unknown>> = {
  component: FC<StepComponentProps<T>>
  validationSchema?: unknown
  showBackButton?: boolean
  renderFooterStart?: FC<StepComponentProps<T>>
  footerActions?: FooterAction<T>[]
}

type SteppedFormProps<T extends Record<string, unknown>> = {
  steps: Step<T>[]
  initialValues: T
  beforeStepAdvance?: (
    values: T,
    stepIndex: number,
    formik: FormikProps<T>,
  ) => boolean | Promise<boolean>
  onStepAdvance?: (
    values: T,
    stepIndex: number,
    formik: FormikProps<T>,
    context: StepAdvanceContext,
  ) => void | Promise<void>
  onFinalSubmit?: (values: T, formik: FormikProps<T>) => void | Promise<void>
  onStepChange?: (step: number) => void
  className?: string
  contentClassName?: string
  footerClassName?: string
}

const defaultContinueAction = <T extends Record<string, unknown>>(): FooterAction<T> => ({
  label: "Continue",
  type: "submit",
  className: "button-primary p-button-sm",
})

const defaultBackAction = <T extends Record<string, unknown>>(): FooterAction<T> => ({
  label: "Back",
  type: "button",
  className:
    "p-button-sm p-button-outlined !border-gray-300 !text-gray-500 hover:!bg-gray-50 !font-semibold focus:!ring-0 focus:!ring-offset-0 focus:!outline-none !shadow-none focus:!bg-gray-50 focus:!text-gray-500",
  onClick: ({ onBack }) => onBack(),
})

const defaultFinalSubmitAction = <T extends Record<string, unknown>>(): FooterAction<T> => ({
  label: "Complete & Continue",
  type: "submit",
  className: "button-primary p-button-sm",
  loading: ({ isSubmitting }) => isSubmitting,
  disabled: ({ isSubmitting }) => isSubmitting,
})

const resolveActionState = <T extends Record<string, unknown>, K extends "loading" | "disabled" | "hidden">(
  action: FooterAction<T>,
  key: K,
  context: FooterActionContext<T>,
) => {
  const value = action[key]
  return typeof value === "function" ? value(context) : value
}

const SteppedForm = <T extends Record<string, unknown>>({
  steps,
  initialValues,
  beforeStepAdvance,
  onStepAdvance,
  onFinalSubmit,
  onStepChange,
  className,
  contentClassName,
  footerClassName,
}: SteppedFormProps<T>) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [formValues, setFormValues] = useState<T>(initialValues)
  const formRef = useRef<HTMLFormElement>(null)
  const formikRef = useRef<FormikProps<T> | null>(null)

  const currentStep = steps[currentStepIndex]
  const isFirstStep = currentStepIndex === 0
  const isLastStep = currentStepIndex === steps.length - 1

  useEffect(() => {
    setFormValues(initialValues)
  }, [initialValues])

  useEffect(() => {
    onStepChange?.(currentStepIndex + 1)
  }, [currentStepIndex, onStepChange])

  const handleBack = () => {
    if (!isFirstStep) {
      setCurrentStepIndex((prev) => prev - 1)
    }
  }

  const handleNext = async (values: T, formik: FormikProps<T>) => {
    const mergedValues = { ...formValues, ...values } as T

    if (isLastStep) {
      await onFinalSubmit?.(mergedValues, formik)
      return
    }

    if (beforeStepAdvance) {
      const canAdvance = await beforeStepAdvance(mergedValues, currentStepIndex, formik)
      if (!canAdvance) return
    }

    const previousStepIndex = currentStepIndex
    const previousValues = structuredClone(formValues)

    setFormValues(mergedValues)
    setCurrentStepIndex((prev) => prev + 1)

    const rollback = () => {
      setCurrentStepIndex(previousStepIndex)
      setFormValues(previousValues)
      formik.resetForm({ values: previousValues })
    }

    await onStepAdvance?.(mergedValues, previousStepIndex, formik, { rollback })
  }

  const footerActions =
    currentStep.footerActions ??
    (isLastStep
      ? [defaultBackAction<T>(), defaultFinalSubmitAction<T>()]
      : isFirstStep
        ? [defaultContinueAction<T>()]
        : [defaultBackAction<T>(), defaultContinueAction<T>()])

  return (
    <Formik
      initialValues={formValues}
      validationSchema={currentStep.validationSchema}
      onSubmit={async (values) => {
        if (!formikRef.current) return
        await handleNext(values as T, formikRef.current)
      }}
      enableReinitialize
      validateOnChange={false}
      validateOnBlur={false}
    >
      {(formik) => {
        formikRef.current = formik

        const stepProps: StepComponentProps<T> = {
          ...formik,
          onNext: () => formRef.current?.requestSubmit(),
          onBack: handleBack,
          isFirstStep,
          isLastStep,
          currentStepIndex,
          totalSteps: steps.length,
        }

        const footerContext: FooterActionContext<T> = {
          onNext: stepProps.onNext,
          onBack: handleBack,
          isSubmitting: formik.isSubmitting,
          requestSubmit: () => formRef.current?.requestSubmit(),
        }

        const FooterStart = currentStep.renderFooterStart

        return (
          <Form
            ref={formRef}
            className={classNames("flex flex-col", className)}
            aria-autocomplete="none"
            autoComplete="off"
          >
            <div className={classNames("p-8", contentClassName)}>
              <currentStep.component {...stepProps} />
            </div>

            <div
              className={classNames(
                "flex gap-4 p-6 border-t border-gray-200",
                {
                  "flex-col md:flex-row md:items-center md:justify-between": FooterStart,
                  "justify-end": !FooterStart,
                },
                footerClassName,
              )}
            >
              {FooterStart && <FooterStart {...stepProps} />}
              <div className="flex justify-end gap-4 shrink-0">
                {footerActions.map((action, index) => {
                  const isHidden = resolveActionState(action, "hidden", footerContext)
                  if (isHidden) return null

                  const showBack = currentStep.showBackButton !== false
                  if (action.label === "Back" && (isFirstStep || !showBack)) return null

                  return (
                    <Button
                      key={`${action.label}-${index}`}
                      type={action.type === "submit" ? "button" : (action.type ?? "button")}
                      label={action.label}
                      className={action.className}
                      loading={!!resolveActionState(action, "loading", footerContext)}
                      disabled={!!resolveActionState(action, "disabled", footerContext)}
                      outlined={action.className?.includes("p-button-outlined")}
                      onClick={() => {
                        if (action.type === "submit") {
                          footerContext.requestSubmit()
                          return
                        }

                        action.onClick?.(footerContext)
                      }}
                    />
                  )
                })}
              </div>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

export { SteppedForm }
export type {
  Step,
  StepComponentProps,
  SteppedFormProps,
  FooterAction,
  StepAdvanceContext,
  FormikErrors,
  FormikTouched,
}
